import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { userEmailSelector } from 'store/selectors';
import { isEmail } from 'helpers';
import Button from '../../Button';
import './TestEmailModal.sass';
import TextFieldTags from '../../Forms/TextFieldTags';

const messages = defineMessages({
  cancelButton: {
    id: 'button.cancel',
    defaultMessage: 'Cancel',
  },
  sendButton: {
    id: 'button.send-test-email',
    defaultMessage: 'Send test email',
  },
  invalidEmailError: {
    id: 'error.invalid-email',
    defaultMessage: 'Please enter valid email',
  },
  emailLabel: {
    id: 'test-email-modal.email-input-label',
    defaultMessage: 'Test email recipients',
  },
  maximumEmailsMessage: {
    id: 'test-email-modal.maximum-emails-message',
    defaultMessage:
      'Enter email and press enter to add it. Maximum {number} emails',
  },
  quotaRemaining: {
    id: 'test-email.quota-remaining',
    defaultMessage: '{remaining}/{limit} remaining',
  },
  quotaExhausted: {
    id: 'test-email.quota-exhausted',
    defaultMessage: 'You have reached the daily limit of {limit} test emails.',
  },
});

const maximumEmailsNumber = 3;

function TestEmailModal({ intl, loading, quota, onSendTestEmails, onClose }) {
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState(null);

  const userEmail = useSelector(userEmailSelector);

  const quotaExhausted = quota?.remaining_quota === 0;

  const effectiveMaxEmails = Math.min(
    maximumEmailsNumber,
    quota?.remaining_quota ?? maximumEmailsNumber,
  );

  const handleResetError = () => {
    setError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (quotaExhausted) {
      return;
    }

    if (!emails.length) {
      setError('Add at least 1 email');
      return;
    }

    handleResetError();
    onSendTestEmails(userEmail, emails);
  };

  const handleValidate = (value) => {
    if (!isEmail(value)) {
      return intl.formatMessage(messages.invalidEmailError);
    }
  };

  return (
    <form className="test-email-modal" onSubmit={handleSubmit}>
      <div className="test-email-modal__content">
        <TextFieldTags
          error={!!error}
          disabled={
            loading || quotaExhausted || emails.length >= effectiveMaxEmails
          }
          placeholder="email@example.com"
          label={intl.formatMessage(messages.emailLabel)}
          aria-label="email"
          name="email"
          helperText={
            error ||
            (quotaExhausted ? (
              <span className="test-email-modal__quota-message test-email-modal__quota-message--error">
                {intl.formatMessage(messages.quotaExhausted, {
                  limit: quota.daily_limit,
                })}
              </span>
            ) : (
              <>
                {intl.formatMessage(messages.maximumEmailsMessage, {
                  number: effectiveMaxEmails,
                })}
              </>
            ))
          }
          value={emails}
          onChange={setEmails}
          onValidate={handleValidate}
          fullWidth
        />
      </div>
      {quota?.remaining_quota != null && quota?.daily_limit != null && (
        <div className="test-email-modal__quota-remaining">
          <span
            className={
              quotaExhausted ? 'test-email-modal__quota-remaining--count' : ''
            }>
            {quota.remaining_quota}
          </span>
          /{quota.daily_limit} remaining
        </div>
      )}
      <div className="test-email-modal__buttons">
        <Button
          type="button"
          className="test-email-modal__button"
          onClick={onClose}>
          {intl.formatMessage(messages.cancelButton)}
        </Button>
        <Button
          type="submit"
          className="test-email-modal__button"
          disabled={loading || quotaExhausted}
          loading={loading}
          onBlur={handleResetError}
          variant="contained">
          {intl.formatMessage(messages.sendButton)}
        </Button>
      </div>
    </form>
  );
}

TestEmailModal.propTypes = {
  intl: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  quota: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSendTestEmails: PropTypes.func.isRequired,
};

export default injectIntl(TestEmailModal);
